'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import AdvertisePopup from '@/components/ads/AdvertisePopup';
interface InterstitialAdProps {
  mobileCode?: string;
  desktopCode?: string;
  index: number;
  /** When true, adds col-span-full class for grid layouts */
  fullWidth?: boolean;
}

/**
 * Generate a 6-character random alphanumeric code
 */
function generateUniqueCode(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Replace div IDs in ad code with unique IDs to prevent conflicts
 * Finds all div-gpt-ad-XXXXX-X patterns and appends unique code
 */
function makeAdCodeUnique(code: string, uniqueCode: string): string {
  if (!code) return '';

  // Find all unique div-gpt-ad IDs in the code
  const divIdPattern = /div-gpt-ad-[\d]+-[\d]+/g;
  const foundIds = new Set<string>();
  let match;
  while ((match = divIdPattern.exec(code)) !== null) {
    foundIds.add(match[0]);
  }

  // Replace each unique ID with a new unique version
  let result = code;
  foundIds.forEach(originalId => {
    const newId = `${originalId}-${uniqueCode}`;
    // Replace all occurrences of this ID (in id attributes, display calls, defineSlot, etc.)
    result = result.split(originalId).join(newId);
  });
  return result;
}

/**
 * Extract ad slot size from GPT ad code (defineSlot call).
 * Returns { width, height } or defaults to { width: 300, height: 250 }.
 */
function extractAdSize(adCode: string): {
  width: number;
  height: number;
} {
  if (!adCode) return {
    width: 300,
    height: 250
  };
  const match = adCode.match(/defineSlot\(\s*['"][^'"]+['"]\s*,\s*(\[[^\]]+\])\s*,/);
  if (!match) return {
    width: 300,
    height: 250
  };
  try {
    const sizes = JSON.parse(match[1]);
    if (Array.isArray(sizes[0])) {
      return {
        width: sizes[0][0],
        height: sizes[0][1]
      };
    }
    if (typeof sizes[0] === 'number' && typeof sizes[1] === 'number') {
      return {
        width: sizes[0],
        height: sizes[1]
      };
    }
  } catch {/* ignore */}
  return {
    width: 300,
    height: 250
  };
}

/**
 * Extract non-script HTML and scripts separately from ad code
 */
function parseAdCode(code: string): {
  html: string;
  scripts: {
    src?: string;
    content?: string;
  }[];
} {
  if (!code) return {
    html: '',
    scripts: []
  };
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = code;
  const scripts: {
    src?: string;
    content?: string;
  }[] = [];
  const scriptElements = tempDiv.querySelectorAll('script');
  scriptElements.forEach(script => {
    if (script.src) {
      scripts.push({
        src: script.src
      });
    } else if (script.textContent) {
      scripts.push({
        content: script.textContent
      });
    }
    script.remove();
  });
  return {
    html: tempDiv.innerHTML,
    scripts
  };
}
export default function InterstitialAd({
  mobileCode,
  desktopCode,
  index,
  fullWidth = false
}: InterstitialAdProps) {
  const uniqueCode = useMemo(() => generateUniqueCode(), []);
  const mobileRef = useRef<HTMLDivElement | null>(null);
  const desktopRef = useRef<HTMLDivElement | null>(null);
  const executedRef = useRef(false);
  const [isClient, setIsClient] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  // Check if ad codes are valid
  const hasMobileCode = !!(mobileCode && mobileCode.trim().length > 0);
  const hasDesktopCode = !!(desktopCode && desktopCode.trim().length > 0);

  // Extract fixed ad sizes from ad codes
  const mobileAdSize = useMemo(() => hasMobileCode ? extractAdSize(mobileCode!) : {
    width: 300,
    height: 250
  }, [mobileCode, hasMobileCode]);
  const desktopAdSize = useMemo(() => hasDesktopCode ? extractAdSize(desktopCode!) : {
    width: 300,
    height: 250
  }, [desktopCode, hasDesktopCode]);

  // Early return for SSR - don't render anything if no valid codes at all
  // This check happens before any hooks that depend on browser APIs
  const hasAnyValidCode = hasMobileCode || hasDesktopCode;

  // Process ad codes to make IDs unique
  const processedMobileCode = useMemo(() => hasMobileCode ? makeAdCodeUnique(mobileCode!, `${uniqueCode}_m${index}`) : '', [mobileCode, hasMobileCode, uniqueCode, index]);
  const processedDesktopCode = useMemo(() => hasDesktopCode ? makeAdCodeUnique(desktopCode!, `${uniqueCode}_d${index}`) : '', [desktopCode, hasDesktopCode, uniqueCode, index]);

  // Determine viewport on client side only
  useEffect(() => {
    const checkViewport = () => {
      const isMobile = window.innerWidth < 768;
      setIsMobileView(isMobile);
    };
    setIsClient(true);
    checkViewport();

    // Listen for resize to handle viewport changes
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Calculate if we should render based on viewport and available codes
  const shouldRender = isClient && hasAnyValidCode && (isMobileView && hasMobileCode || !isMobileView && hasDesktopCode);

  // Execute ad scripts after component mounts and viewport is determined
  useEffect(() => {
    if (!isClient || !shouldRender || executedRef.current) return;
    executedRef.current = true;
    const executeAdScripts = (code: string, container: HTMLDivElement | null) => {
      if (!code || !container) return;
      const {
        html,
        scripts
      } = parseAdCode(code);

      // Set HTML content
      container.innerHTML = html;

      // Execute scripts
      scripts.forEach(scriptInfo => {
        try {
          if (scriptInfo.src) {
            // Check if GPT script is already loaded
            if (scriptInfo.src.includes('gpt.js') && (window as any).googletag) {
              return; // Skip loading GPT again
            }
            const newScript = document.createElement('script');
            newScript.src = scriptInfo.src;
            newScript.async = true;
            document.head.appendChild(newScript);
          } else if (scriptInfo.content) {
            // Execute inline script
            try {
              const fn = new Function(scriptInfo.content);
              fn();
            } catch {
              // Fallback: create script element
              const newScript_0 = document.createElement('script');
              newScript_0.textContent = scriptInfo.content;
              document.head.appendChild(newScript_0);
              newScript_0.remove();
            }
          }
        } catch (err) {
          console.error('[InterstitialAd] Script execution error:', err);
        }
      });
    };

    // Execute based on viewport
    if (isMobileView && hasMobileCode && processedMobileCode) {
      executeAdScripts(processedMobileCode, mobileRef.current);
    } else if (!isMobileView && hasDesktopCode && processedDesktopCode) {
      executeAdScripts(processedDesktopCode, desktopRef.current);
    }
  }, [isClient, shouldRender, isMobileView, hasMobileCode, hasDesktopCode, processedMobileCode, processedDesktopCode]);

  // Don't render anything if no valid ad code for current viewport
  if (!shouldRender) {
    return null;
  }
  return <div className={`interstitial-ad-container my-2${fullWidth ? ' col-span-full' : ''}`}>
      <div className="bg-gray-100 p-[5px] relative">
        <div className="flex justify-center">
          {/* Mobile Ad - only render if we have mobile code and are on mobile */}
          {isMobileView && hasMobileCode && <div ref={mobileRef} className="flex items-center justify-center" style={{
          width: mobileAdSize.width,
          height: mobileAdSize.height
        }} data-ad-type="mobile" data-ad-index={index} />}

          {/* Desktop Ad - only render if we have desktop code and are on desktop */}
          {!isMobileView && hasDesktopCode && <div ref={desktopRef} className="flex items-center justify-center" style={{
          width: desktopAdSize.width,
          height: desktopAdSize.height
        }} data-ad-type="desktop" data-ad-index={index} />}
        </div>

        {/* Advertise with us - bottom left corner */}
        <AdvertisePopup />
      </div>
    </div>;
}